import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import {
  AUDIT_LOG_DETAILS_ACTION_COLUMN,
  AUDIT_LOG_ROW_DETAIL_EXTRAS,
  auditLogDetailValue,
  buildAuditListQueryParams,
  mapAuditLogRowToDisplay,
  normalizeAuditListResponse,
} from './audit-log-datasource.utils';

export function audit_logs_user(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  _router: unknown
) {
  const buildActivityDescription = (row: any): string => {
    const parts = [row.field_label, row.change_type_label].filter(Boolean);
    const base = parts.join(' — ');
    const from =
      row.value_from_display && row.value_from_display !== '-' ? row.value_from_display : '';
    const to = row.value_to_display && row.value_to_display !== '-' ? row.value_to_display : '';
    const extra: string[] = [];
    if (from) {
      extra.push(`fra: ${from}`);
    }
    if (to) {
      extra.push(`til: ${to}`);
    }
    if (extra.length) {
      return `${base} (${extra.join(', ')})`;
    }
    return base || `${row.entity_name || ''} / ${row.field_name || ''}`;
  };

  return {
    name: 'User Audit Logs',
    description: 'Audit logs for a specific user (requires userUuid parameter)',
    execute: (params?: LoadDataParams) => {
      const userUuidFilter = params?.filters?.find((f) => f.column === 'userUuid');
      const userUuid = userUuidFilter?.value;

      if (!userUuid) {
        console.error('[audit_logs_user] No userUuid provided');
        return of({ items: [], totalCount: 0, error: 'User UUID is required' });
      }

      const svc = componentDataSourceService as any;
      const request = svc['convertToListRequest'](
        { ...params, dataSourceKey: 'audit_logs_user' },
        'audit_logs_user'
      );
      const queryParams = buildAuditListQueryParams(request);
      const endpoint = `${componentDataSourceService.apiUrl}/audit-log/user/${userUuid}`;

      return http.get<any>(endpoint, { params: queryParams }).pipe(
        catchError((error) => {
          console.error('Error fetching user audit logs:', error);
          return of({ items: [], totalCount: 0 });
        }),
        map((result: any) => {
          const norm = normalizeAuditListResponse(result, request.pageSize);
          if (!norm.items.length) {
            return {
              items: [],
              totalCount: norm.totalCount,
              pageSize: norm.pageSize,
              pageIndex: norm.pageIndex,
              user_uuid: userUuid,
            };
          }
          try {
            const auditLogs = norm.items.map((log: any) => {
              const mapped = mapAuditLogRowToDisplay(log);
              return {
                ...mapped,
                changed_by_uuid: mapped.changed_by?.uuid || '',
                user_uuid: userUuid,
                action_description: buildActivityDescription(mapped),
              };
            });

            return {
              items: auditLogs,
              totalCount: norm.totalCount,
              pageSize: norm.pageSize,
              pageIndex: norm.pageIndex,
              filters: result?.filters,
              user_uuid: userUuid,
            };
          } catch (error) {
            console.error('Error processing user audit logs:', error);
            return { items: [], totalCount: 0, pageSize: norm.pageSize, pageIndex: norm.pageIndex };
          }
        })
      );
    },
    settings: {
      columns: [
        {
          key: 'changed_at',
          header: 'Hvornår',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return 'N/A';
            }
            return new Date(value).toLocaleString('da-DK');
          },
          sortable: true,
          width: '180px',
        },
        {
          key: 'action_description',
          header: 'Aktivitet',
          type: 'text',
          width: '260px',
        },
        {
          key: 'affected_user_name',
          header: 'Berørt',
          type: 'text',
          transform: (name: string, row: any) => {
            if (!name || name === '-') {
              return '-';
            }
            if (row.affected_user_email) {
              return `${name} (${row.affected_user_email})`;
            }
            return name;
          },
          sortable: true,
          width: '200px',
        },
        {
          key: 'what_label',
          header: 'Hvad',
          type: 'text',
          sortable: true,
          width: '120px',
        },
        {
          key: 'field_label',
          header: 'Ændring',
          type: 'text',
          sortable: true,
          width: '160px',
        },
        {
          key: 'change_type_label',
          header: 'Handling',
          type: 'text',
          sortable: true,
          width: '100px',
        },
        {
          key: 'changed_by_name',
          header: 'Udført af',
          type: 'text',
          transform: (value: string, row: any) => {
            if (row.changed_by_uuid && row.user_uuid && row.changed_by_uuid === row.user_uuid) {
              return (value || 'Ukendt') + ' (dig selv)';
            }
            return value || 'Ukendt';
          },
          sortable: true,
          width: '160px',
        },
        {
          key: 'value_from_display',
          header: 'Fra',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return '-';
            }
            if (value.length > 80) {
              return value.substring(0, 80) + '…';
            }
            return value;
          },
          detailTransform: auditLogDetailValue,
          width: '140px',
        },
        {
          key: 'value_to_display',
          header: 'Til',
          type: 'text',
          transform: (value: string) => {
            if (!value) {
              return '-';
            }
            if (value.length > 80) {
              return value.substring(0, 80) + '…';
            }
            return value;
          },
          detailTransform: auditLogDetailValue,
          width: '140px',
        },
        AUDIT_LOG_DETAILS_ACTION_COLUMN,
      ],
      rowDetailExtras: AUDIT_LOG_ROW_DETAIL_EXTRAS,
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'changed_at',
      defaultSortDirection: 'desc',
    },
  };
}
